import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Car, UserRound } from 'lucide-react';
import type { Job } from '../types/models';
import { missingCompletionFields } from '../lib/completion';

export function CourtesyCarTag({ compact = false }: { compact?: boolean }) {
  return <span className="job-tag tag-courtesy"><Car size={14} aria-hidden="true" />{compact ? 'CC Req' : 'Courtesy car'}</span>;
}

export function AssigneeTag({ job }: { job: Job }) {
  const subcontracted = job.assignmentType === 'subcontractor' || !!job.assignedSubcontractorName;
  const name = subcontracted ? job.assignedSubcontractorName : job.assignedUserDisplayName;
  if (!name) return <span className="text-[14px] text-ink3">Unassigned</span>;
  const Icon = subcontracted ? Building2 : UserRound;
  return <span className={`job-tag ${subcontracted ? 'tag-subcontracted' : 'tag-neutral'}`}><Icon size={14} aria-hidden="true" />{name}</span>;
}

export function PhotoTypeTag({ type, label, overlay = false }: { type: string; label: string; overlay?: boolean }) {
  const kind = ['collection', 'delivery', 'courtesy'].includes(type) ? type : 'additional';
  const iconOnly = overlay && kind === 'courtesy';
  return <span className={`photo-type-tag photo-type-${kind} ${overlay ? 'photo-type-overlay' : ''} ${iconOnly ? 'photo-type-icon' : ''}`}
    role={iconOnly ? 'img' : undefined} aria-label={iconOnly ? 'Courtesy car' : undefined}>
    {iconOnly ? <Car size={15} aria-hidden="true" /> : label}
  </span>;
}

/** The button is separate from the row link: opening the list must not open the job. */
export function NeedsDetailsMark({ job, withText = false }: { job: Job; withText?: boolean }) {
  const missing = missingCompletionFields(job);
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{left: number; top: number} | null>(null);
  useEffect(() => {
    if (!position) return;
    panel.current?.focus({preventScroll: true});
    const outside = (event: PointerEvent) => {
      if (!button.current?.contains(event.target as Node) && !panel.current?.contains(event.target as Node)) setPosition(null);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setPosition(null); button.current?.focus(); }
    };
    const close = () => setPosition(null);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', close);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', key); window.removeEventListener('resize', close); };
  }, [position]);
  if (!missing.length) return null;
  return <>
    <button ref={button} type="button" className="needs-details-control" aria-label={withText ? 'Needs details' : `Needs details for ${job.registrationNumber || 'job'}`}
      aria-expanded={!!position} aria-controls={position ? id : undefined} onClick={() => {
        if (position) { setPosition(null); return; }
        const bounds = button.current!.getBoundingClientRect();
        const width = Math.min(22 * parseFloat(getComputedStyle(document.documentElement).fontSize), window.innerWidth - 32);
        setPosition({left: Math.max(16, Math.min(bounds.left, window.innerWidth - width - 16)), top: Math.max(16, Math.min(bounds.bottom + 8, window.innerHeight - 240))});
      }}>
      <span className="needs-details-circle" aria-hidden="true">!</span>
      {withText && <span className="text-[14px] text-ink">Needs details</span>}
    </button>
    {position && createPortal(<div ref={panel} id={id} role="region" tabIndex={-1} aria-label="Missing job details" className="needs-details-popover" style={{...position, maxHeight: window.innerHeight - position.top - 16}}>
      <div className="mb-2 text-[14px] font-semibold text-ink">Needs details</div>
      <ul className="list-inside list-disc space-y-1 text-[14px] text-ink2">{missing.map(item => <li key={item.field}>{item.label}</li>)}</ul>
    </div>, document.body)}
  </>;
}
